/**
 * smartSearchService.ts
 *
 * Employee finder for the HR console (§L.3 — every row links to the
 * specific record). Two layers:
 *
 *   1. Local fuzzy search — builds an in-memory index from data the
 *      console already loaded (submissions / appraisals / appeals +
 *      directory for emails) and filters it client-side. Deterministic,
 *      instant, no network.
 *
 *   2. AI query parsing — natural-language queries ("siapa di Commercial
 *      yang belum submit?") go to the parseEprSearchQuery Cloud Function
 *      (DeepSeek `deepseek-chat`), which returns a ParsedSearchFilter.
 *      ONLY the query text leaves the browser — filtering still happens
 *      here, locally, so employee data is never sent to the model.
 */

import { getFunctions, httpsCallable, type HttpsCallable } from 'firebase/functions';
import type { EPRSubmission } from '../types/eprFramework';
import type { AppraisalForm } from '../types/appraisal';
import type { AppealRecord } from '../types/appeal';
import type { DirectoryEmployee } from '../types/magicLink';

// ─── Index ──────────────────────────────────────────────────────────

export interface EmployeeSearchRow {
  employeeId: string;
  name: string;
  email?: string;
  department?: string;
  position?: string;
  /** Step 1 progress. */
  step1: 'self_pending' | 'manager_pending' | 'submitted';
  /** Step 5 progress — undefined until an appraisal exists. */
  signOff?: 'pending' | 'employee_signed' | 'done';
  /** Step 6 — an appeal is currently open. */
  appealOpen: boolean;
  /** Step 7 — outcome recorded on the appraisal. */
  hasOutcome: boolean;
}

/** Merge the cycle's documents into one searchable row per employee. */
export function buildEmployeeIndex(
  submissions: EPRSubmission[],
  appraisals: AppraisalForm[],
  openAppeals: AppealRecord[],
  directory: DirectoryEmployee[],
): EmployeeSearchRow[] {
  const dirById = new Map(directory.map(d => [d.id, d]));
  const appraisalByEmp = new Map(appraisals.map(a => [a.employeeId, a]));
  const appealEmpIds = new Set(openAppeals.map(a => a.employeeId));

  return submissions
    .map(s => {
      const dir = dirById.get(s.employeeId);
      const appraisal = appraisalByEmp.get(s.employeeId);
      return {
        employeeId: s.employeeId,
        name:       s.header?.employeeName ?? dir?.name ?? s.employeeId,
        email:      dir?.email,
        department: s.header?.department ?? dir?.unitName,
        position:   s.header?.position ?? dir?.jobTitle,
        step1:
          s.status === 'draft' ? 'self_pending' as const :
          s.status === 'employee_submitted' ? 'manager_pending' as const :
          'submitted' as const,
        signOff: appraisal
          ? (appraisal.status === 'manager_signed' || appraisal.status === 'closed'
              ? 'done' as const
              : appraisal.status === 'employee_signed'
                ? 'employee_signed' as const
                : 'pending' as const)
          : undefined,
        appealOpen: appealEmpIds.has(s.employeeId),
        hasOutcome: !!appraisal?.outcomeId,
      };
    })
    .sort((a, b) => a.name.localeCompare(b.name));
}

// ─── Local fuzzy filter ─────────────────────────────────────────────

/** Every space-separated token must match name, email, dept or position. */
export function fuzzyFilter(rows: EmployeeSearchRow[], query: string): EmployeeSearchRow[] {
  const tokens = query.toLowerCase().split(/\s+/).filter(Boolean);
  if (tokens.length === 0) return rows;
  return rows.filter(r => {
    const haystack = [r.name, r.email, r.department, r.position]
      .filter(Boolean).join(' ').toLowerCase();
    return tokens.every(t => haystack.includes(t));
  });
}

// ─── AI-parsed filter ───────────────────────────────────────────────

// Keep in sync with FILTER_STATUSES in functions/src/ai/parseSearchQuery.ts
export type ParsedSearchStatus =
  | 'self_pending'
  | 'manager_pending'
  | 'submitted'
  | 'sign_pending'
  | 'appeal_open'
  | 'no_outcome';

export interface ParsedSearchFilter {
  nameContains: string | null;
  department: string | null;
  status: ParsedSearchStatus | null;
}

export function applyParsedFilter(
  rows: EmployeeSearchRow[],
  filter: ParsedSearchFilter,
): EmployeeSearchRow[] {
  return rows.filter(r => {
    if (filter.nameContains) {
      const hay = `${r.name} ${r.email ?? ''}`.toLowerCase();
      if (!hay.includes(filter.nameContains.toLowerCase())) return false;
    }
    if (filter.department) {
      if (!r.department?.toLowerCase().includes(filter.department.toLowerCase())) return false;
    }
    switch (filter.status) {
      case 'self_pending':    return r.step1 === 'self_pending';
      case 'manager_pending': return r.step1 === 'manager_pending';
      case 'submitted':       return r.step1 === 'submitted';
      case 'sign_pending':    return r.signOff !== undefined && r.signOff !== 'done';
      case 'appeal_open':     return r.appealOpen;
      case 'no_outcome':      return !r.hasOutcome;
      case null:              return true;
    }
  });
}

/** Human-readable echo of the filter, so HR sees what the AI understood. */
export function describeParsedFilter(filter: ParsedSearchFilter): string {
  const parts: string[] = [];
  if (filter.status) {
    const labels: Record<ParsedSearchStatus, string> = {
      self_pending:    'self-assessment not submitted',
      manager_pending: 'manager review pending',
      submitted:       'fully submitted',
      sign_pending:    'sign-off pending',
      appeal_open:     'appeal open',
      no_outcome:      'no outcome yet',
    };
    parts.push(labels[filter.status]);
  }
  if (filter.department) parts.push(`department ~ "${filter.department}"`);
  if (filter.nameContains) parts.push(`name ~ "${filter.nameContains}"`);
  return parts.length > 0 ? parts.join(' · ') : 'no filter detected';
}

// ─── Cloud Function call ────────────────────────────────────────────

interface ParseQueryInput { query: string }

let _callable: HttpsCallable<ParseQueryInput, ParsedSearchFilter> | null = null;
function getCallable(): HttpsCallable<ParseQueryInput, ParsedSearchFilter> {
  if (!_callable) {
    const functions = getFunctions(undefined, 'us-central1');
    _callable = httpsCallable<ParseQueryInput, ParsedSearchFilter>(
      functions,
      'parseEprSearchQuery',
    );
  }
  return _callable;
}

/** Sends ONLY the query text to the Cloud Function / DeepSeek. */
export async function parseSearchQueryWithAi(query: string): Promise<ParsedSearchFilter> {
  const result = await getCallable()({ query });
  return result.data;
}
